import _ from "lodash";
import { ORDER_STATUS_CREATED } from "../constants/OrderConstant";

/**
 * The order search on the orders page (HVC-90): finds an order of either shop when a customer calls
 * or mails without their order number, by order number, email, name, company, phone or postcode.
 *
 * Firestore has no "contains" and compares case-sensitively: a field can only be matched exactly, or
 * on how it starts (a range from `x` to `x + `). So the input becomes a handful of targeted
 * queries, with the spellings the webshops actually store (counted on the dev data, 2026-10-29):
 *
 * | Field       | Stored as                                                                  |
 * |-------------|----------------------------------------------------------------------------|
 * | orderId     | `HVC-1234-567890-1234` (VCE) or `HVO-...` (HVO): 4-6-4 digits               |
 * | email       | always lowercase (both checkouts lowercase it)                             |
 * | phoneNumber | as typed: nearly always `0612345678`, sometimes `+31612345678`             |
 * | names       | as typed, mostly capitalised; a tussenvoegsel stays in lastName: "de Vries" |
 * | zipCode     | `1611AB`, `1611ab` or `1611 AB`                                            |
 *
 * Known gaps, accepted for now: "vries" does not find "de Vries" (the last name starts with "de"),
 * and a phone number in a notation nobody uses yet is missed. Closing them needs a normalised search
 * field written at checkout.
 *
 * Only paid orders come back: CREATED (payment never completed) is left out, like on the orders page.
 *
 * Pure: no Firestore, no React. useOrderSearch runs the queries.
 */

/** Fewer characters than this is not a search. */
export const MIN_SEARCH_LENGTH = 2;
/**
 * Per query and per shop. Firestore hands a query's matches back in document id order, not by date,
 * so a query that hits its limit may have left newer orders behind. An order number, email, phone or
 * postcode points at one customer, whose orders should all come back before they are sorted; a name
 * prefix can match half the customers ("jan"), so it stops sooner.
 */
export const CUSTOMER_QUERY_LIMIT = 200;
export const NAME_QUERY_LIMIT = 50;
/** What the result list shows at most, newest first. */
export const MAX_RESULTS = 50;

export const SEARCH_ORDER_NUMBER = "order_number";
export const SEARCH_PHONE_OR_ORDER_NUMBER = "phone_or_order_number";
export const SEARCH_EMAIL = "email";
export const SEARCH_POSTCODE = "postcode";
export const SEARCH_TEXT = "text";

const ORDER_ID_PREFIXES = ["HVC-", "HVO-"];
const ORDER_ID_SEGMENTS = [4, 6, 4];

const FIELD_ORDER_ID = "orderId";
const FIELD_EMAIL = "customer.email";
const FIELD_PHONE = "customer.phoneNumber";
const FIELD_FIRST_NAME = "customer.firstName";
const FIELD_LAST_NAME = "customer.lastName";
const FIELD_COMPANY = "customer.address.delivery.companyName";
const FIELD_ZIP_CODE = "customer.address.delivery.zipCode";

const normalise = (input) =>
  String(input || "")
    .trim()
    .replace(/\s+/g, " ");
const capitalise = (word) => word.charAt(0).toUpperCase() + word.slice(1);

/**
 * The ways a name can be stored, for a "starts with" query: as typed, lowercase, capitalised, every
 * word capitalised, and only the last word capitalised ("de Vries", "van der Berg").
 */
export const spellings = (text) => {
  const typed = normalise(text);
  const lower = typed.toLowerCase();
  const words = lower.split(" ");
  return _.uniq([
    typed,
    lower,
    capitalise(lower),
    words.map(capitalise).join(" "),
    [...words.slice(0, -1), capitalise(words[words.length - 1])].join(" "),
  ]).filter(Boolean);
};

/**
 * The notations a phone number can be stored in, for an exact match. A Dutch number is brought back
 * to its ten national digits first, so `+31 6 1234 5678`, `0031612345678` and `06-12345678` all
 * look for the same set. A foreign number is only looked for as typed and as bare digits.
 */
export const phoneVariants = (input) => {
  const typed = normalise(input);
  const digits = typed.replace(/\D/g, "");
  const international = typed.startsWith("+") ? `00${digits}` : digits;

  let national = null;
  if (international.startsWith("0031")) national = `0${international.slice(4)}`;
  else if (international.startsWith("31") && international.length === 11) national = `0${international.slice(2)}`;
  else if (international.startsWith("0") && !international.startsWith("00")) national = international;
  else if (international.length === 9) national = `0${international}`;

  if (!national || national.length !== 10) return _.uniq([typed, digits, `+${digits}`]);
  const subscriber = national.slice(1);
  return _.uniq([national, `+31${subscriber}`, `31${subscriber}`, `0031${subscriber}`, subscriber, typed]);
};

/** `1611 ab` → every way that postcode is stored. */
export const postcodeVariants = (digits, letters) => {
  const upper = letters.toUpperCase();
  const lower = letters.toLowerCase();
  return _.uniq([
    `${digits}${upper}`,
    `${digits}${lower}`,
    `${digits} ${upper}`,
    `${digits} ${lower}`,
    `${digits}${upper.charAt(0)}${lower.charAt(1)}`,
  ]);
};

/**
 * The digits of an order number, typed in whole or from the start, as the start of a stored order
 * number: `1234567890` → `1234-567890`. With the shop prefix typed only that shop's, else both.
 */
export const orderIdPrefixes = (prefix, digits) => {
  const segments = [];
  let rest = digits;
  ORDER_ID_SEGMENTS.forEach((length) => {
    if (rest.length > 0) segments.push(rest.slice(0, length));
    rest = rest.slice(length);
  });
  const shops = prefix ? [`${prefix.toUpperCase()}-`] : ORDER_ID_PREFIXES;
  return shops.map((shop) => `${shop}${segments.join("-")}`);
};

const prefixQueries = (field, values) => values.map((value) => ({ field, op: "prefix", value }));

const everything = () => true;

/** Gives every query the limit that fits the kind of search. */
const searchOf = (kind, queries, matches = everything) => {
  const limit = kind === SEARCH_TEXT ? NAME_QUERY_LIMIT : CUSTOMER_QUERY_LIMIT;
  return { kind, queries: queries.map((query) => ({ ...query, limit })), matches };
};

const ORDER_NUMBER_PATTERN = /^(hv[co])?[\s-]*([\d\s-]+)$/i;
const PHONE_PATTERN = /^\+?[\d\s\-().]+$/;
const POSTCODE_PATTERN = /^(\d{4})\s?([a-z]{2})(?:\s*(\d+)\s?\S*)?$/i;

/** 06 12345678 has 10 digits, 0031 6 12345678 13; a whole order number has 14. */
const isPhoneLength = (digits) => digits.length >= 9 && digits.length <= 13;

const houseNumberOf = (order) => String(order?.customer?.address?.delivery?.houseNumber ?? "").match(/^\d+/)?.[0];

const searchableText = (order) => {
  const customer = order?.customer || {};
  return [customer.firstName, customer.lastName, customer.address?.delivery?.companyName, customer.email]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
};

/**
 * Turns what was typed into the queries to run on each shop, and the check every order that comes
 * back must pass (a query on the first name alone also brings every other Jan).
 *
 * @returns {{ kind: string, queries: Array<{field: string, op: "prefix"|"in", value: *, limit: number}>,
 *   matches: Function }} `kind` null and no queries when the input is too short to search on.
 */
export const buildOrderSearch = (input) => {
  const typed = normalise(input);
  if (typed.length < MIN_SEARCH_LENGTH) return searchOf(null, []);

  if (typed.includes("@")) return searchOf(SEARCH_EMAIL, prefixQueries(FIELD_EMAIL, [typed.toLowerCase()]));

  const postcode = typed.match(POSTCODE_PATTERN);
  if (postcode) {
    const [, digits, letters, houseNumber] = postcode;
    return searchOf(
      SEARCH_POSTCODE,
      [{ field: FIELD_ZIP_CODE, op: "in", value: postcodeVariants(digits, letters) }],
      (order) => !houseNumber || houseNumberOf(order) === houseNumber,
    );
  }

  const orderNumber = typed.match(ORDER_NUMBER_PATTERN);
  if (orderNumber) {
    const [, prefix, rest] = orderNumber;
    const digits = rest.replace(/\D/g, "");
    const queries = digits.length > 0 ? prefixQueries(FIELD_ORDER_ID, orderIdPrefixes(prefix, digits)) : [];
    // Digits alone can also be a phone number, which never carries a shop prefix
    if (!prefix && isPhoneLength(digits)) {
      queries.push({ field: FIELD_PHONE, op: "in", value: phoneVariants(typed) });
      return searchOf(SEARCH_PHONE_OR_ORDER_NUMBER, queries);
    }
    return searchOf(SEARCH_ORDER_NUMBER, queries);
  }

  // +31 6 12345678, (0228) 123456
  if (PHONE_PATTERN.test(typed)) {
    const digits = typed.replace(/\D/g, "");
    return searchOf(
      SEARCH_PHONE_OR_ORDER_NUMBER,
      isPhoneLength(digits) ? [{ field: FIELD_PHONE, op: "in", value: phoneVariants(typed) }] : [],
    );
  }

  const typedWords = typed.split(" ");
  const words = typed.toLowerCase().split(" ");
  const queries = [
    ...prefixQueries(FIELD_LAST_NAME, spellings(typed)),
    ...prefixQueries(FIELD_FIRST_NAME, spellings(typedWords[0])),
    ...prefixQueries(FIELD_COMPANY, spellings(typed)),
  ];
  // "jan de vries": first name "jan", last name "de vries"
  if (words.length > 1) queries.push(...prefixQueries(FIELD_LAST_NAME, spellings(typedWords.slice(1).join(" "))));
  if (words.length === 1) queries.push(...prefixQueries(FIELD_EMAIL, [words[0]]));

  return searchOf(
    SEARCH_TEXT,
    _.uniqBy(queries, (query) => `${query.field}|${query.value}`),
    (order) => {
      const text = searchableText(order);
      return words.every((word) => text.includes(word));
    },
  );
};

/**
 * The orders all queries of both shops brought back, as one list: each order once, only paid orders
 * that pass the search's own check, newest first, at most `MAX_RESULTS`.
 *
 * @param {Object[]} orders - Tagged with `shopSource`, duplicates allowed
 * @param {{ matches: Function }} search - From buildOrderSearch
 * @param {boolean} hitLimit - Whether any query came back full
 * @returns {{ orders: Object[], total: number, incomplete: boolean }} `total` is how many were found,
 *   also beyond `MAX_RESULTS`; `incomplete` means more may exist than were fetched, so the list may
 *   miss newer orders too.
 */
export const collectSearchResults = (orders, search, hitLimit = false) => {
  const found = _.uniqBy(orders || [], (order) => `${order.shopSource}|${order.id}`)
    .filter((order) => order.orderStatus !== ORDER_STATUS_CREATED)
    .filter((order) => search.matches(order))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  return { orders: found.slice(0, MAX_RESULTS), total: found.length, incomplete: hitLimit };
};
